import { useRef, useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { GroupPhoto, isUploaded } from '../../components/GroupPhoto.tsx';
import { SearchBox } from '../../components/SearchBox.tsx';
import { ErrorState, Loading } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { createGroup, patchGroup, usePickableFriends } from '../../api/rooms.ts';
import type { Group, GroupPhoto as PhotoKey, Person } from '../../domain.ts';
import { PhotoDefaults, PhotoSourcePick } from './PhotoPicker.tsx';
import { ImageError, toSquareDataUrl } from './pickImage.ts';

/**
 * F2 모임 만들기 · F3-1-1 모임 수정.
 *
 * **한 화면입니다.** 받는 것이 같고(사진 · 이름 · 한 줄 소개 · 구성원) 배치도
 * 같아서, 화면 둘로 나누면 같은 폼이 두 군데서 따로 낡습니다. 갈리는 것은
 * 제목과 저장 버튼의 글씨, 그리고 처음에 채워져 있는 값뿐입니다.
 *
 * 사진 칸은 **왼쪽 끝**, 이름 · 소개는 그 오른쪽에 세로로 둡니다. 사진이 줄
 * 하나를 따로 차지하지 않아 구성원 목록이 위로 올라옵니다.
 *
 * 구성원은 **내 친구 중에서만** 고릅니다 — 차단한 상대는 목록에 뜨지 않습니다.
 */

/**
 * 사진을 고르는 동안 어디까지 왔는가.
 *
 * 사진첩에는 «단계»가 없습니다 — 파일 선택 창은 OS 가 띄우고, 고르면 바로
 * 사진 칸에 들어옵니다. 우리가 그릴 화면이 없어 단계로 두지 않았습니다.
 */
type PhotoStep = 'none' | 'source' | 'defaults';

interface Props {
  /** 수정이면 지금 값, 만들기면 없음 */
  group?: Group;
  onCancel: () => void;
  onSaved: (group: Group) => void;
}

export function GroupForm({ group, onCancel, onSaved }: Props) {
  const editing = group !== undefined;

  const [name, setName] = useState(group?.name ?? '');
  const [intro, setIntro] = useState(group?.intro ?? '');
  // 기본 사진의 키(F2-3)이거나 사진첩에서 고른 그림의 데이터 URL(F2-2)입니다
  const [photo, setPhoto] = useState<PhotoKey | string | undefined>(group?.photo);
  // 파일 선택 창을 코드로 여는 자리. 화면에는 보이지 않습니다
  const fileRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Set<string>>(
    // 수정이면 그룹장(나)을 뺀 나머지가 지금 구성원입니다 — 그룹장은 고를 것이 아닙니다
    new Set(group?.members.filter((m) => m.userId !== group.ownerId).map((m) => m.userId)),
  );
  const [query, setQuery] = useState('');
  const [step, setStep] = useState<PhotoStep>('none');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const { data: friends, loading, error, reload } = usePickableFriends();

  const found = (friends ?? []).filter((f) =>
    (f.name ?? '').toLowerCase().includes(query.trim().toLowerCase()),
  );

  const toggle = (userId: string) =>
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  /**
   * 사진첩에서 고른 파일을 사진 칸에 넣습니다 — F2-2.
   *
   * 원본을 그대로 담지 않고 정사각 256px 로 줄입니다. 저장은 «저장»을 누를
   * 때 한꺼번에 하므로 여기서는 화면의 값만 바꿉니다 — 고르자마자 서버에
   * 올리면 ✕ 로 나갔을 때 되돌릴 것이 생깁니다.
   */
  const takePhoto = async (file: File) => {
    setFailure(null);
    try {
      setPhoto(await toSquareDataUrl(file));
    } catch (e) {
      setFailure(e instanceof ImageError ? e.message : '사진을 쓰지 못했습니다.');
    }
  };

  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const members = [...picked];
      onSaved(
        editing
          ? await patchGroup(group.groupId, {
              name: name.trim(),
              intro,
              // 사진을 뗀 것과 안 건드린 것을 갈라 보냅니다
              ...(photo === undefined ? { clearPhoto: true } : { photo }),
              members,
            })
          : await createGroup({
              name: name.trim(),
              intro,
              ...(photo === undefined ? {} : { photo }),
              members,
            }),
      );
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '저장하지 못했습니다.');
      setBusy(false);
    }
  };

  /* ── F2-3 은 이 화면을 대신 채웁니다 ─────────────────────── */

  if (step === 'defaults') {
    return (
      <PhotoDefaults
        photo={photo !== undefined && isUploaded(photo) ? undefined : (photo as PhotoKey | undefined)}
        name={name}
        onBack={() => setStep('none')}
        onPick={(key) => {
          setPhoto(key);
          setStep('none');
        }}
      />
    );
  }

  return (
    <div className="screen">
      <NavBar
        title={editing ? '모임 수정' : '모임 만들기'}
        subtitle={editing ? group.name : undefined}
        start={{ icon: 'close', label: '나가기', onClick: onCancel }}
        end={{ icon: 'check', label: '저장', accent: true, onClick: save }}
      />

      <div className="screen__body">
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        {/* 사진은 왼쪽 끝, 이름 · 소개는 오른쪽에 세로로 */}
        <div className="phrow">
          <div className="field phrow__col">
            <span className="field__label">모임 사진</span>
            <button
              type="button"
              className={photo === undefined ? 'phslot' : 'phslot phslot--filled'}
              aria-label={photo === undefined ? '모임 사진 고르기' : '모임 사진 바꾸기'}
              onClick={() => setStep('source')}
            >
              {photo === undefined ? (
                <span className="phslot__plus">
                  <Icon name="plus" size={17} />
                </span>
              ) : (
                <>
                  <GroupPhoto photo={photo} />
                  {/* 오른쪽 아래 연필이 «바꿀 수 있다»는 표시입니다 */}
                  <span className="phslot__edit">
                    <Icon name="pencil" size={13} />
                  </span>
                </>
              )}
            </button>
          </div>

          <div className="phrow__fields">
            <label className="field">
              <span className="field__label">모임 이름</span>
              <input
                className="input"
                value={name}
                autoFocus={!editing}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">한 줄 소개</span>
              <input
                className="input"
                value={intro}
                placeholder="모임을 소개해 주세요 (선택)"
                onChange={(e) => setIntro(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="field">
          <span className="field__label">구성원 · {picked.size}명 선택됨</span>
          <SearchBox value={query} onChange={setQuery} placeholder="친구 이름으로 찾기" />
        </div>

        {loading && <Loading label="친구를 불러오는 중" />}
        {error && <ErrorState message={error} onRetry={reload} />}

        {!loading && (friends ?? []).length === 0 && (
          <div className="empty">
            <p className="empty__title">고를 친구가 없습니다</p>
            <p className="empty__hint">
              친구를 먼저 추가해 주세요. 차단한 상대는 여기 나오지 않습니다.
            </p>
          </div>
        )}

        <div className="stack stack--tight">
          {found.map((f) => (
            <MemberPick
              key={f.userId}
              person={f}
              on={picked.has(f.userId)}
              onToggle={() => toggle(f.userId)}
            />
          ))}
        </div>

        {/* 기획서 3.5.2 의 권한 구조를 여기서 미리 알려 줍니다 */}
        <div className="card card--flat">
          <p className="hint">
            {editing ? '내가' : '만든 사람이'} <b>그룹장</b>입니다. 기록지를 만들고 지우는 것,
            구성원을 넣고 빼는 것, 구성원의 기도 제목을 대신 적는 것이 그룹장의 몫입니다.
            <br />
            <b>그룹장은 위임하지 않으면 모임을 나갈 수 없습니다.</b>
          </p>
        </div>

        <div className="btn-row btn-row--foot">
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || name.trim() === ''}
            onClick={save}
          >
            {busy ? '저장 중…' : editing ? '저장' : '모임 만들기'}
          </button>
        </div>
      </div>

      {step === 'source' && (
        <PhotoSourcePick
          onLibrary={() => {
            setStep('none');
            fileRef.current?.click();
          }}
          onDefaults={() => setStep('defaults')}
          onClose={() => setStep('none')}
        />
      )}

      {/*
        F2-2 — 사진첩. 설계는 이 자리를 네이티브 사진 선택 브리지로 잡아
        두었지만, 웹앱으로 홀로 서면서 브리지가 필요 없어졌습니다. 브라우저의
        파일 선택이 곧 사진첩이고, 권한 창도 OS 가 알아서 띄웁니다.

        화면에는 보이지 않습니다 — 위의 「사진첩에서 선택」이 코드로 엽니다.
      */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          // 같은 파일을 다시 골라도 onChange 가 오게 비웁니다
          e.target.value = '';
          if (file) void takePhoto(file);
        }}
      />
    </div>
  );
}

/** 눌러서 켜고 끄는 구성원 줄 — F2 · F3-3 이 함께 씁니다 */
export function MemberPick({
  person,
  on,
  tag,
  onToggle,
}: {
  person: Person;
  on: boolean;
  /** 「나 · 그룹장」 같은 꼬리표 */
  tag?: string;
  onToggle?: () => void;
}) {
  return (
    <button
      type="button"
      className="pickrow"
      aria-pressed={on}
      disabled={onToggle === undefined}
      onClick={onToggle}
    >
      <Avatar name={person.name ?? '?'} seed={person.userId} />
      <span className="pickrow__name">
        {person.name ?? '이름 없음'}
        {tag && <span className="rec__tag">{tag}</span>}
      </span>
      <span className={on ? 'chk chk--on' : 'chk'}>{on && <Icon name="check" size={12} />}</span>
    </button>
  );
}
